/**
 * HOW MUCH ROOM TO RESERVE UNDER A PAGE THAT ENDS BENEATH A FIXED BAR.
 *
 * The budget page's totals bar is `position: fixed` and renders whenever a
 * version is selected — i.e. essentially always, not only when there are
 * unsaved changes. Something has to stop the last content coming to rest
 * underneath it at the bottom of the scroll.
 *
 * ═══ THE MEASUREMENTS THIS REPLACED A GUESS WITH ═════════════════════════
 * Chromium, 1440x900, the budget page's Labour Flow tab with the Gantt shown,
 * 30 crew members. Everything below the chart, in document pixels:
 *
 *     16   the Labour Flow section's own bottom padding
 *     24   `space-y-6`'s margin-top on the spacer
 *     64   the spacer itself (`h-16`)
 *     24   the page container's `lg:p-6` bottom padding
 *     41   THE APP'S OWN `<footer>` (`hidden lg:block`)
 *    ---
 *    169   total trailing space below the chart
 *
 * against an occluder of (at the time; the fade is 5rem / 80px since
 * 2026-10-05 — the arithmetic reads the MEASURED fade, so nothing below moves):
 * a 106px fade (`calc(6rem + 10px)`, its top 25%
 * transparent) and a 48px bar sitting `bottom-4`, so the bar occupies the
 * bottom 64px of the viewport. At maximum scroll the chart's bottom edge sat
 * at viewport y=731 with the bar's top edge at y=836 — **a 105px gap**, which
 * is what the owner reported as *"a big space between the bottom of the chart
 * and the bottom sticky totals bar"*.
 *
 * 🔻 THREE THINGS THAT WERE BELIEVED ABOUT THIS AND ARE FALSE. They are
 * recorded because each one, on its own, would send the next person the wrong
 * way — and all three were argued from the source before anybody measured.
 *
 *   1. **"Delete the spacer and the chart goes under the totals bar."** It
 *      does not. Measured with the spacer AND its margin removed, the chart's
 *      bottom lands at viewport y=819 against a bar top of y=836 — still 17px
 *      CLEAR of the bar. What it does go under is the FADE (25px into it),
 *      which is a real regression but a different one.
 *   2. **"On a short page the reservation is dead space the owner can see."**
 *      It is invisible there. On a short tab the document is exactly the
 *      viewport height and does not scroll at all, so the spacer adds height
 *      that never becomes scrollable, inside a gap that is already ~400px.
 *      The over-reservation is visible ONLY when the page scrolls.
 *   3. **"The spacer is what protects the last content."** It never protected
 *      the LAST content. The app's `<footer>` sits below it and comes to rest
 *      at viewport y=859–900 — underneath both the fade and the bar — and
 *      always has. A reservation that leaves the final element occluded was
 *      not doing the job it was named for, which is the real argument for
 *      replacing it rather than merely shrinking it.
 *
 * ═══ WHY THIS IS MEASURED AND NOT A CONSTANT ═════════════════════════════
 * `h-16` is 64px, which is almost exactly the bar's own occupied height — so
 * as "reserve the occluder" it was defensible. The waste is not the spacer:
 * it is that **three other mechanisms already reserve 89px between the last
 * content and the end of the document**, and the spacer double-counted all of
 * them. Two of those three (`space-y-6`, the container padding) are page-wide
 * and must not be touched for this; the third is a `<footer>` whose height is
 * not ours to know. So the only honest constant is the one nobody can write
 * down, and the reservation has to be **what is still missing** rather than a
 * number.
 *
 * That is why the inputs are geometry rather than settings, and why the result
 * is stable: see `reservedElsewhere`.
 */

/**
 * @param {object}  m
 * @param {number}  m.occluderPx    Height of the thing that covers content —
 *   the fade's own measured height, not a guess and not the bar's.
 * @param {number}  m.reservedElsewhere  Everything between the last content
 *   and the end of the document that is NOT the spacer: the spacer's own
 *   collapsed margin plus whatever follows it (container padding, footer).
 *   🔑 THIS IS INVARIANT UNDER THE SPACER'S HEIGHT, which is the whole reason
 *   the loop converges: growing the spacer grows `scrollHeight` by exactly the
 *   same amount, so re-measuring after applying the result reproduces the
 *   result. No damping, no epsilon, no second pass.
 * @param {number}  m.contentBottom  Document-space bottom edge of the last
 *   real content (the spacer's top, less its margin).
 * @param {number}  m.documentHeightWithoutSpacer
 * @param {number}  m.viewportHeight
 * @returns {number|null} Pixels to reserve, or `null` when the geometry is not
 *   measurable yet — the caller must fall back to a safe non-zero reservation,
 *   never to 0. "Unmeasured" and "nothing needed" are different answers and
 *   collapsing them puts content under the bar on the very first paint.
 */
export function saveBarSpacerHeight({
  occluderPx,
  reservedElsewhere,
  contentBottom,
  documentHeightWithoutSpacer,
  viewportHeight,
}) {
  const inputs = [occluderPx, reservedElsewhere, contentBottom, documentHeightWithoutSpacer, viewportHeight];
  if (!inputs.every((n) => Number.isFinite(n))) return null;
  // A zero-height viewport is a detached/hidden document, not a short page.
  if (viewportHeight <= 0) return null;

  // 🔑 RESERVE NOTHING WHEN THE CONTENT NEVER REACHES THE BAR. Scroll the page
  // as far as it will go with NO spacer at all: if the last content still
  // comes to rest above the occluder, there is nothing to protect it from and
  // every pixel reserved is dead document. This is the short-tab case, and it
  // is why the answer is 0 there rather than a smaller constant.
  const maxScrollWithoutSpacer = Math.max(0, documentHeightWithoutSpacer - viewportHeight);
  const contentBottomAtFullScroll = contentBottom - maxScrollWithoutSpacer;
  if (contentBottomAtFullScroll <= viewportHeight - occluderPx) return 0;

  // Otherwise reserve exactly the shortfall. `Math.ceil` errs INTO the
  // reservation: a fractional pixel too much is invisible, a fractional pixel
  // too little leaves a hairline of content under the fade.
  return Math.max(0, Math.ceil(occluderPx - reservedElsewhere));
}

export default saveBarSpacerHeight;
